import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, CheckCircle2, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import FileUploadField from "../components/FileUploadField";
import { api } from "../lib/api";

export default function StoryPage() {
    const [form, setForm] = useState({
        name: "", email: "", title: "", body: "", image_url: "", location: "", tags: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.title.trim() || !form.body.trim()) {
            return toast.error("Name, email, title and story are all required");
        }
        if (form.body.trim().split(/\s+/).length < 20) {
            return toast.error("Story is a bit short — give us at least 20 words");
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            await api.post("/stories", payload);
            setDone(true);
            toast.success("Story received. Our editors will review it.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-black text-white">
                <SEO title="Story Submitted" description="Thanks for sharing your story with MyStreetzMag." />
                <Header />
                <main className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center">
                    <CheckCircle2 className="w-20 h-20 text-streetz-yellow mx-auto mb-6" />
                    <h1 className="font-display uppercase text-4xl md:text-5xl">Story received</h1>
                    <p className="text-streetz-muted mt-4 max-w-md mx-auto">
                        Our editors will read it and reach out if we&apos;re publishing it.
                        Expect a reply within 3–5 days.
                    </p>
                    <div className="flex justify-center gap-3 mt-8">
                        <Link to="/" data-testid="story-back-home" className="overline bg-streetz-red text-white px-6 py-3 hover:bg-white hover:text-black">BACK HOME</Link>
                        <button
                            onClick={() => { setDone(false); setForm({ name: "", email: "", title: "", body: "", image_url: "", location: "", tags: "" }); }}
                            data-testid="story-submit-another"
                            className="overline border border-streetz-line px-6 py-3 hover:border-streetz-yellow"
                        >
                            SUBMIT ANOTHER
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title="Upload Your Story"
                description="Got a story to share? Lagos street life, a music memory, a culture take — MyStreetzMag wants to hear it."
            />
            <Header />
            <main className="max-w-3xl mx-auto px-4 md:px-8 py-12">
                <Link to="/" data-testid="story-back-link" className="overline text-streetz-yellow inline-flex items-center gap-2 hover:text-white mb-6">
                    <ArrowLeft className="w-3 h-3" /> BACK HOME
                </Link>

                <div className="flex items-center gap-3 mb-3">
                    <BookOpen className="w-7 h-7 text-streetz-yellow" />
                    <h1 className="font-display uppercase text-4xl md:text-5xl">Upload your story</h1>
                </div>
                <p className="text-streetz-muted mb-8 max-w-xl">
                    Got something to share? A music memory, a street culture take, a moment from Lagos
                    or Abuja or anywhere the sound travels — we want it. The best ones get edited and
                    published in MyStreetzMag.
                </p>

                <form onSubmit={submit} className="bg-streetz-paper border border-streetz-line p-6 md:p-8 flex flex-col gap-5" data-testid="story-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex flex-col gap-1">
                            <span className="overline text-[10px]">Your name *</span>
                            <input value={form.name} onChange={handle("name")} required data-testid="story-name" className="bg-black border border-streetz-line px-3 py-2" />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="overline text-[10px]">Your email *</span>
                            <input type="email" value={form.email} onChange={handle("email")} required data-testid="story-email" className="bg-black border border-streetz-line px-3 py-2" />
                        </label>
                    </div>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Story title *</span>
                        <input value={form.title} onChange={handle("title")} required data-testid="story-title" placeholder="What's the headline?" className="bg-black border border-streetz-line px-3 py-2" />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">The story * (at least 20 words)</span>
                        <textarea value={form.body} onChange={handle("body")} required rows={10} data-testid="story-body" placeholder="Tell us everything. Names, places, dates if it helps." className="bg-black border border-streetz-line px-3 py-2 font-sans" />
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex flex-col gap-1">
                            <span className="overline text-[10px]">Location (city / country)</span>
                            <input value={form.location} onChange={handle("location")} data-testid="story-location" placeholder="Lagos, Nigeria" className="bg-black border border-streetz-line px-3 py-2" />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="overline text-[10px]">Tags (comma-separated)</span>
                            <input value={form.tags} onChange={handle("tags")} data-testid="story-tags" placeholder="afrobeats, lagos, memory" className="bg-black border border-streetz-line px-3 py-2" />
                        </label>
                    </div>
                    <label className="flex flex-col gap-1">
                        <span className="overline text-[10px]">Photo URL (optional, or upload below)</span>
                        <input value={form.image_url} onChange={handle("image_url")} data-testid="story-image-url" placeholder="https://…" className="bg-black border border-streetz-line px-3 py-2" />
                        <FileUploadField
                            endpoint="/uploads/article-image"
                            accept="image/jpeg,image/png,image/webp"
                            label="Or upload a photo"
                            testId="story-photo-upload"
                            onUploaded={(url) => setForm({ ...form, image_url: url })}
                        />
                    </label>
                    <p className="text-[11px] text-streetz-muted">
                        By submitting you agree to our <Link to="/terms" className="text-streetz-yellow underline">Terms</Link> — your story stays yours,
                        but you give us the right to edit and publish it across MyStreetzMag and our partner platforms.
                    </p>
                    <button
                        disabled={submitting}
                        type="submit"
                        data-testid="story-submit"
                        className="self-start overline inline-flex items-center gap-2 bg-streetz-red text-white px-6 py-3 hover:bg-white hover:text-black disabled:opacity-60"
                    >
                        {submitting ? "SENDING…" : (<><Send className="w-4 h-4" /> SUBMIT STORY</>)}
                    </button>
                </form>
            </main>
            <Footer />
        </div>
    );
}
